import { defineStore } from 'pinia'
import { ref, Ref } from 'vue'
import debounce from 'lodash-es/debounce'
import cloneDeep from 'lodash-es/cloneDeep'
import { WatermarkRows, StatusItems, Documents, ScrollMode, ConfigStatus } from '@/pages/admin/documentTools/types.ts'
import {
  rowIsIncomplete,
  getRowIndex,
  getAcceptedConfig,
  allConfigsRejected,
  updateConfigStatus,
  getInitialWatermarkRows,
  updateConfig,
  updateWatermarkRowStatus,
  getInternalRow,
} from '@/pages/admin/documentTools/helpers/watermarkHelpers.ts'

const DEBOUNCE_AMOUNT = 100

export const useWatermarkStore = defineStore('Watermark', () => {
  const cleanPdfPageCount: Ref<number> = ref(0)
  const cleanPdfPage: Ref<number> = ref(1)
  const currentDocName: Ref<string | undefined> = ref(undefined)
  const currentConfigName: Ref<string | undefined> = ref(undefined)
  const watermarkRows: Ref<WatermarkRows> = ref([])
  const scrollMode: Ref<ScrollMode> = ref(ScrollMode.ONE_PAGE)
  const pageSetByScrolling: Ref<boolean> = ref(false)
  const errorMessage = ref<string | undefined>(undefined)
  const discard = ref<{ show: true; nextRoute: string } | undefined>(undefined)

  const setPageCount = (pages: number) => {
    cleanPdfPageCount.value = pages
  }

  const setPage = (pageNumber: number) => {
    cleanPdfPage.value = pageNumber
  }

  const setCurrentDocName = (docName: string) => {
    currentDocName.value = docName
  }

  const setCurrentConfigName = (config: string) => {
    currentConfigName.value = config
  }

  const incrementConfig = (increment: number) => {
    const currentDocIndex = watermarkRows.value.findIndex((row) => row.documentName === currentDocName.value)
    const currentConfigIndex = watermarkRows.value[currentDocIndex].likelyConfig.findIndex(
      (config) => config.name === currentConfigName.value,
    )
    const nextConfig = watermarkRows.value[currentDocIndex].likelyConfig[currentConfigIndex + increment]
    if (nextConfig) {
      currentConfigName.value = nextConfig.name
    }
  }

  const acceptDoc = (docName: string | undefined, configName: string | undefined) => {
    if (!docName || !configName) return
    const watermarkRowsCopy = cloneDeep(watermarkRows.value)
    const updatedRows = updateConfigStatus({
      watermarkRows: watermarkRowsCopy,
      documentName: docName,
      configName,
      status: ConfigStatus.APPROVED,
    })
    watermarkRows.value = updatedRows
  }

  const rejectDoc = (docName: string | undefined, configName: string | undefined) => {
    if (!docName || !configName) return
    const watermarkRowsCopy = cloneDeep(watermarkRows.value)
    const updatedRows = updateConfigStatus({
      watermarkRows: watermarkRowsCopy,
      documentName: docName,
      configName,
      status: ConfigStatus.REJECTED,
    })
    watermarkRows.value = updatedRows
  }

  const setAcceptedConfig = async () => {
    const watermarkRowsCopy = cloneDeep(watermarkRows.value)
    watermarkRowsCopy.map((row) => {
      const acceptedConfig = getAcceptedConfig(row)
      const rejected = allConfigsRejected(row)
      if (acceptedConfig) {
        row.acceptedConfig = { name: acceptedConfig.name, cleanPdfUrl: acceptedConfig.cleanPdfUrl }
      } else if (rejected) {
        row.acceptedConfig = { name: ConfigStatus.REJECTED }
      }
    })
    watermarkRows.value = watermarkRowsCopy
  }

  const handlePageIncrement = debounce((increment: number) => {
    const currentPage = cleanPdfPage.value
    const totalPageCount = cleanPdfPageCount.value
    const newIndex = currentPage + increment
    setPage(newIndex < 1 ? 1 : newIndex > totalPageCount ? totalPageCount : newIndex)
  }, DEBOUNCE_AMOUNT)

  const setInitialWatermarkRows = (documents: Documents) => {
    watermarkRows.value = getInitialWatermarkRows(documents)
  }

  const updateWatermarkRow = async ({
    processIdentifier,
    documentName,
    statusItems,
  }: {
    processIdentifier: string
    documentName: string
    statusItems?: StatusItems
  }) => {
    const incompleteRow = watermarkRows.value.filter((row) => rowIsIncomplete(row, processIdentifier))[0]
    let updatedRow = await updateConfig({
      watermarkRow: incompleteRow,
      docName: documentName,
      statusItems: statusItems,
    })
    updatedRow = updateWatermarkRowStatus({ watermarkRow: updatedRow })
    const rowsCopy = cloneDeep(watermarkRows.value)
    const index = getRowIndex(rowsCopy, updatedRow.documentName)
    if (index !== -1) {
      rowsCopy[index] = updatedRow
    }
    watermarkRows.value = rowsCopy
  }

  const updateInternalWatermarkRows = (results) => {
    let rowsCopy = cloneDeep(watermarkRows.value)

    results.forEach((result: { documentName: string; url?: string }) => {
      const index = getRowIndex(rowsCopy, result.documentName)
      const isAlreadyLikely = rowsCopy[index].likelyConfig.find((config) => config.name === 'internal')
      const isAlreadyNoOp = rowsCopy[index].noOpConfig.includes('internal')
      if (result.url && !isAlreadyLikely) {
        rowsCopy[index].likelyConfig.push(getInternalRow(result.url))
      } else if (!isAlreadyNoOp) {
        rowsCopy[index].noOpConfig.push('internal')
      }
    })
    rowsCopy = rowsCopy.map((row) => updateWatermarkRowStatus({ watermarkRow: row }))
    watermarkRows.value = rowsCopy
  }

  const setScrollMode = (newMode: ScrollMode) => {
    scrollMode.value = newMode
  }

  const setScrollingPage = (pageNumber: number) => {
    cleanPdfPage.value = pageNumber
    pageSetByScrolling.value = true
    setTimeout(() => {
      pageSetByScrolling.value = false
    }, 100)
  }

  const setErrorMessage = (message: string | undefined) => {
    errorMessage.value = message
  }

  const reset = () => {
    cleanPdfPageCount.value = 0
    cleanPdfPage.value = 1
    currentDocName.value = undefined
    currentConfigName.value = undefined
    watermarkRows.value = []
    scrollMode.value = ScrollMode.ONE_PAGE
    pageSetByScrolling.value = false
    errorMessage.value = undefined
    discard.value = undefined
  }

  const setShowDiscard = (discardData) => {
    discard.value = discardData
  }

  return {
    acceptDoc,
    errorMessage,
    cleanPdfPage,
    cleanPdfPageCount,
    currentConfigName,
    currentDocName,
    handlePageIncrement,
    incrementConfig,
    rejectDoc,
    scrollMode,
    setAcceptedConfig,
    setCurrentConfigName,
    setCurrentDocName,
    setErrorMessage,
    setInitialWatermarkRows,
    setPage,
    setPageCount,
    setScrollMode,
    setScrollingPage,
    updateInternalWatermarkRows,
    updateWatermarkRow,
    watermarkRows,
    reset,
    setShowDiscard,
    discard,
  }
})
